import {
  Box,
  Button,
  Checkbox,
  InputAdornment,
  TextField,
  Typography,
  CircularProgress,
  Skeleton,
} from "@mui/material";
import React, { useState, useRef, useEffect } from "react";
import {
  LogoDarkMode,
} from "../assets/images";
import Grid from "@mui/material/Grid2";
import { styled } from "@mui/material/styles";
import { CheckboxProps } from "@mui/material/Checkbox";
import "react-international-phone/style.css";
import AuthService from "../services/auth.service";
import { useNavigate, useSearchParams } from "react-router-dom";
import AccountVerification from "../modals/AccountVerification";
import moment from "moment-timezone";
import { CardComponent } from "@chargebee/chargebee-js-react-wrapper";
import chargebeeService from "../services/chargebee.service";
import { Applications } from "../types/enums";
import { toast } from "react-toastify";
import { SocialProvider } from "../types/auth";
import { config } from "../config";
import theme from "../theme";
import {
  LIVE_PRICES,
  AUTOMATED_PRICES,
  LIVE_AUTO_PRICES,
  PricingTier,
} from "../components/subscription/pricing/pricingData";
import {
  validateEmail,
  generateSecurePassword,
  validateAndSanitizeName,
  sanitizeTextInput,
} from "../utils/common.util";
import { LeadsquaredService } from "../services/leadsquared/leadsquared.service";
import { PLAN_NAMES } from "../constants/planNames";
import { ArrowForward } from "@mui/icons-material";
import EventsLoggerService from "../services/events-logger.service";
import {
  SIGNUP_FLOWS,
  logEvent,
  EVENT_TYPES,
  SIGNUP_STEPS,
} from "../utils/events.util";
import MuiPhone from "../components/MuiPhone";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}


const Signup = () => {
  const navigate = useNavigate();
  const [showPromoField, setShowPromoField] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [leadSquaredId, setLeadSquaredId] = useState<string>("");

  const getTimezoneString = () => {
    const timezone = moment.tz.guess() || "UTC";
    const offset = moment.tz(timezone).format("Z");
    const zoneName = moment.tz(timezone).zoneName();
    return `(GMT${offset}),${timezone},${zoneName}`;
  };

  const generateFirstName = (email: string) => {
    const emailParts = email.split("@")[0];
    const name = emailParts
      .split(/[._-]/)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join(" ");
    return name;
  };

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    phoneNumber: "",
    email: "",
    password: "",
    timezone: getTimezoneString(),
    subdomain: "",
  });

  // Auto-generate strong password on component mount
  useEffect(() => {
    const autoGeneratedPassword = generateSecurePassword();
    setFormData((prev) => ({
      ...prev,
      password: autoGeneratedPassword,
    }));
  }, []);
  const [formErrors, setFormErrors] = useState({
    firstName: "",
    lastName: "",
    phoneNumber: "",
    email: "",
    password: "",
    subdomain: "",
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isAutoRegistering, setIsAutoRegistering] = useState(false);
  const [isCardReady, setIsCardReady] = useState(false);
  const [isCardValid, setIsCardValid] = useState(false);

  const [verificationData, setVerificationData] = useState({
    companyId: "",
    companyName: "",
    infLeadSquaredId: "",
  });

  const [cardToken, setCardToken] = useState<string>("");
  const [cardError, setCardError] = useState<string | null>(null);
  const cardRef = useRef<any>(null);

  const [searchParams, setSearchParams] = useSearchParams();
  const [planDetails, setPlanDetails] = useState<any>(null);
  const [isLoadingPlan, setIsLoadingPlan] = useState(false);
  const [isTrial, setIsTrial] = useState(false);
  const [noTrial, setNoTrial] = useState(false);
  const [monthlyPlanDetails, setMonthlyPlanDetails] = useState<any>(null);
  const [initialPlanId, setInitialPlanId] = useState<string | null>(null);

  const [promoCode, setPromoCode] = useState("");
  const [appliedPromo, setAppliedPromo] = useState<{
    code: string;
    discount: number;
    type: string;
  } | null>(null);
  const [promoError, setPromoError] = useState("");

  const [domainSuggestions, setDomainSuggestions] = useState<string[]>([]);

  const planId = searchParams.get("planId");
  const cleanPlanId = planId ? planId.replace(/-\d+$/, "") : undefined;

  // Initialize billing period from URL
  const initializeBillingPeriodFromUrl = () => {
    if (cleanPlanId) {
      return cleanPlanId.includes("Yearly");
    }
    return true; // Default to yearly
  };

  const [isYearly, setIsYearly] = useState(initializeBillingPeriodFromUrl());

  // Update plan in URL when billing period changes
  const updatePlanInUrl = (newIsYearly: boolean) => {
    if (!planId) return;

    // Extract the attendee number from the current planId
    const attendeeMatch = planId.match(/-(\d+)$/);
    const attendeeNumber = attendeeMatch ? attendeeMatch[1] : "200"; // Default to 250 if no number found

    // Extract the base plan ID without period and attendee number
    const basePlanId = planId
      .replace(/(Monthly|Yearly)/g, "")
      .replace(/-\d+$/, "");
    const period = newIsYearly ? "Yearly" : "Monthly";
    const newPlanId = `${basePlanId}${period}-${attendeeNumber}`;

    setSearchParams(
      (prev) => {
        prev.set("planId", newPlanId);
        return prev;
      },
      { replace: true }
    );
  };

  const handleBillingPeriodChange = (newIsYearly: boolean) => {
    // If the user clicks on the already selected option, do nothing
    if (isYearly === newIsYearly) {
      return;
    }

    setIsYearly(newIsYearly);
    updatePlanInUrl(newIsYearly);

    // Swap the plan details without making API calls
    if (planDetails && monthlyPlanDetails) {
      if (newIsYearly) {
        // Switching to yearly: set planDetails to yearly, monthlyPlanDetails to monthly
        setPlanDetails(monthlyPlanDetails); // monthlyPlanDetails contains yearly data
        setMonthlyPlanDetails(planDetails); // planDetails contains monthly data
      } else {
        // Switching to monthly: set planDetails to monthly, monthlyPlanDetails to yearly
        setPlanDetails(monthlyPlanDetails); // monthlyPlanDetails contains monthly data
        setMonthlyPlanDetails(planDetails); // planDetails contains yearly data
      }
    }
  };

  // Set default planId if not present and track initial plan ID
  useEffect(() => {
    if (!planId) {
      // Use setSearchParams to update only the planId without reloading the page
      setSearchParams(
        (prev) => {
          prev.set("planId", "All-In-One-USD-Yearly-200");
          return prev;
        },
        { replace: true }
      );
    } else if (!initialPlanId) {
      // Set the initial plan ID only once
      setInitialPlanId(planId);
    }

    // Check if this is a free trial plan
    setIsTrial(planId === PLAN_NAMES.FREE_TRIAL);
  }, [planId, setSearchParams, initialPlanId]);

  // Store planId in localStorage when component mounts
  useEffect(() => {
    if (planId) {
      localStorage.setItem("planId", planId);
    }
  }, [planId]);

  useEffect(() => {
    const fetchPlanDetails = async () => {
      if (!initialPlanId) return;

      const cleanInitialPlanId = initialPlanId.replace(/-\d+$/, "");
      setIsLoadingPlan(true);
      try {
        // Fetch both monthly and yearly plan details at once using clean plan IDs without numbers
        const basePlanId = cleanInitialPlanId.replace(/(Monthly|Yearly)/g, "");
        const monthlyPlanId = `${basePlanId}Monthly`;
        const yearlyPlanId = `${basePlanId}Yearly`;

        // Fetch both plans in parallel
        const [monthlyResponse, yearlyResponse] = await Promise.all([
          chargebeeService.getPlanById(monthlyPlanId),
          chargebeeService.getPlanById(yearlyPlanId),
        ]);

        // Store both plan details
        setPlanDetails(isYearly ? yearlyResponse.data : monthlyResponse.data);
        setMonthlyPlanDetails(
          isYearly ? monthlyResponse.data : yearlyResponse.data
        );
      } catch (error) {
        console.error("Failed to fetch plan details:", error);
        // Fallback: try to fetch just the current plan
        try {
          const response =
            await chargebeeService.getPlanById(cleanInitialPlanId);
          setPlanDetails(response.data);
        } catch (fallbackError) {
          console.error(
            "Failed to fetch fallback plan details:",
            fallbackError
          );
        }
      } finally {
        setIsLoadingPlan(false);
      }
    };

    fetchPlanDetails();
  }, [initialPlanId]); // Only depend on initialPlanId, not planId

  useEffect(() => {
    // Extract plan details from planId and console log them
    if (planId) {
      // Parse the planId
      const parts = planId.split("-");
      let extractedPlanType: string;
      let currency: string;
      let duration: string;
      let extractedAttendees: number;
      let liveAttendees: number = 0;
      let automatedAttendees: number = 0;

      if (parts[0] === "All") {
        extractedPlanType = PLAN_NAMES.ALL_IN_ONE; // "All-In-One"
        currency = parts[3]; // USD
        duration = parts[4]; // Yearly/Monthly
        extractedAttendees = parseInt(parts[5]); // e.g., 750

        // Find the matching plan to get the label
        const selectedPlan = LIVE_AUTO_PRICES.find(
          (plan) => plan.value === extractedAttendees.toString()
        );
        if (selectedPlan) {
          // Parse the label to extract live and automated attendees, handling comma-separated numbers
          const labelMatch = selectedPlan.label.match(
            /([\d,]+)\s+Live\s+&\s+([\d,]+)\s+Automated/
          );
          if (labelMatch) {
            // Remove commas and convert to numbers
            liveAttendees = parseInt(labelMatch[1].replace(/,/g, ""));
            automatedAttendees = parseInt(labelMatch[2].replace(/,/g, ""));
          }
        }
      } else {
        extractedPlanType = parts[0] + "-" + parts[1]; // Could be LIVE_WEBINARS or AUTOMATED_GOLD
        currency = parts[2]; // USD
        duration = parts[3]; // Yearly/Monthly
        extractedAttendees = parseInt(parts[4]); // e.g., 250
      }

      // Validate that the extracted plan type is one of the valid plan names
      const validPlanTypes = [
        PLAN_NAMES.LIVE_WEBINARS,
        PLAN_NAMES.AUTOMATED_GOLD,
        PLAN_NAMES.ALL_IN_ONE,
      ];

      if (!validPlanTypes.includes(extractedPlanType)) {
        console.log(
          "Invalid plan type:",
          extractedPlanType,
          "redirecting to pricing page"
        );
        window.location.href = "https://easywebinar.com/pricing/";
        return;
      }

      // Get pricing data based on plan type
      let pricingData: PricingTier[];
      let selectedPlan: PricingTier | undefined;

      switch (extractedPlanType) {
        case PLAN_NAMES.LIVE_WEBINARS:
          pricingData = LIVE_PRICES;
          selectedPlan = pricingData.find(
            (plan: PricingTier) => plan.value === extractedAttendees.toString()
          );
          break;
        case PLAN_NAMES.AUTOMATED_GOLD:
          pricingData = AUTOMATED_PRICES;
          selectedPlan = pricingData.find(
            (plan: PricingTier) => plan.value === extractedAttendees.toString()
          );
          break;
        case PLAN_NAMES.ALL_IN_ONE:
          pricingData = LIVE_AUTO_PRICES;
          selectedPlan = pricingData.find(
            (plan: PricingTier) => plan.value === extractedAttendees.toString()
          );
          break;
        default:
          console.log("Unknown plan type:", extractedPlanType);
          return;
      }

      if (!selectedPlan) {
        console.log(
          "No matching plan found for attendees:",
          extractedAttendees
        );
        return;
      }

      // Update the features object
      const features = {
        planType: extractedPlanType,
        currency,
        duration,
        attendees: extractedAttendees ?? 0,
        liveAttendees: liveAttendees,
        automatedAttendees: automatedAttendees,
        price:
          duration === "Yearly"
            ? (selectedPlan.annual ?? 0)
            : (selectedPlan.monthly ?? 0),
        storage: selectedPlan.storage,
        webinars: "Unlimited",
      };

      // Get plan-specific features based on plan type
      let planFeatures = [];
      switch (extractedPlanType) {
        case PLAN_NAMES.LIVE_WEBINARS:
          planFeatures = [
            `Up to ${extractedAttendees} attendees`,
            "Unlimited live webinars",
            "Custom landing pages + smart scheduling",
            `Up to ${selectedPlan.storage} storage space`,
            "Share offers, files & links",
            "Polls, Q&A, and emoji reactions",
          ];
          break;
        case PLAN_NAMES.AUTOMATED_GOLD:
          planFeatures = [
            `Up to ${extractedAttendees} attendees`,
            "Unlimited automated events",
            "Just-in-time scheduling",
            "Preloaded + live chats & real-time replays",
            "Automated, segmented email campaigns",
            "Data export & analytics",
          ];
          break;
        case PLAN_NAMES.ALL_IN_ONE:
          planFeatures = [
            `Up to ${extractedAttendees} total attendees`,
            "Unlimited live & automated webinars",
            "Turn replays into automated webinars",
            "Auto-played polls, CTAs, and chat prompts",
            "Priority support & success onboarding",
            "CRM Tool Integrations",
          ];
          break;
      }

      // Update all state variables at once to prevent multiple re-renders
      setPlanFeatures(features);
      setPlanType(extractedPlanType);
      setLeads(extractedAttendees);
      setLiveAttendees(liveAttendees);
      setAutomatedAttendees(automatedAttendees);
      setPlanPrice(
        duration === "Yearly"
          ? (selectedPlan.annual ?? 0)
          : (selectedPlan.monthly ?? 0)
      );
      setPlanWebinars("Unlimited");
      setPlanStorage(selectedPlan.storage);
    }
  }, [planId]);

  const calculateDynamicPricing = () => {
    if (isLoadingPlan || !planDetails) {
      return {
        currentPrice: "$00.00",
        alternativePrice: "$00.00",
        savings: "$00.00",
        savingsPercentage: 0,
        isYearlySelected: isYearly,
        currentPeriod: isYearly ? "year" : "month",
        alternativePeriod: isYearly ? "month" : "year",
      };
    }

    const quantity = planId ? parseInt(planId.match(/-(\d+)$/)?.[1] || "0") : 0;

    if (planId === PLAN_NAMES.FREE_TRIAL) {
      return {
        currentPrice: "$00.00",
        alternativePrice: "$00.00",
        savings: "$00.00",
        savingsPercentage: 0,
        isYearlySelected: isYearly,
        currentPeriod: isYearly ? "year" : "month",
        alternativePeriod: isYearly ? "month" : "year",
      };
    }

    // Find the current plan tier
    const currentTier = planDetails?.tiers.find((tier: any) => {
      if (!tier.ending_unit) {
        return quantity >= tier.starting_unit;
      }
      return quantity >= tier.starting_unit && quantity <= tier.ending_unit;
    });

    if (!currentTier) {
      return {
        currentPrice: "$00.00",
        alternativePrice: "$00.00",
        savings: "$00.00",
        savingsPercentage: 0,
        isYearlySelected: isYearly,
        currentPeriod: isYearly ? "year" : "month",
        alternativePeriod: isYearly ? "month" : "year",
      };
    }

    // Get current plan price
    const currentPrice = currentTier.price / 100; // Convert cents to dollars
    let discountedCurrentPrice = currentPrice;

    // Apply discount if promo code is applied
    if (appliedPromo) {
      if (appliedPromo.type === "percentage") {
        discountedCurrentPrice =
          currentPrice * (1 - appliedPromo.discount / 100);
      } else if (appliedPromo.type === "fixed_amount") {
        discountedCurrentPrice = Math.max(
          0,
          currentPrice - appliedPromo.discount / 100
        );
      }
    }

    // Calculate alternative pricing
    let alternativePrice = 0;
    let alternativeTier = null;

    if (isYearly) {
      // If yearly is selected, calculate monthly equivalent
      if (monthlyPlanDetails) {
        // Use the fetched monthly plan details
        alternativeTier = monthlyPlanDetails?.tiers.find((tier: any) => {
          if (!tier.ending_unit) {
            return quantity >= tier.starting_unit;
          }
          return quantity >= tier.starting_unit && quantity <= tier.ending_unit;
        });

        if (alternativeTier) {
          alternativePrice = alternativeTier.price / 100; // Convert cents to dollars

          // Apply same discount to alternative price
          if (appliedPromo) {
            if (appliedPromo.type === "percentage") {
              alternativePrice =
                alternativePrice * (1 - appliedPromo.discount / 100);
            } else if (appliedPromo.type === "fixed_amount") {
              alternativePrice = Math.max(
                0,
                alternativePrice - appliedPromo.discount / 100
              );
            }
          }

          // Calculate yearly equivalent of monthly price
          alternativePrice = alternativePrice * 12;
        } else {
          // Fallback: estimate monthly price as 1/12 of yearly price
          alternativePrice = currentPrice;
        }
      } else {
        // Fallback: estimate monthly price as 1/12 of yearly price
        alternativePrice = currentPrice;
      }
    } else {
      // If monthly is selected, calculate yearly equivalent
      if (monthlyPlanDetails) {
        // Use the fetched monthly plan details
        alternativeTier = monthlyPlanDetails?.tiers.find((tier: any) => {
          if (!tier.ending_unit) {
            return quantity >= tier.starting_unit;
          }
          return quantity >= tier.starting_unit && quantity <= tier.ending_unit;
        });

        if (alternativeTier) {
          alternativePrice = alternativeTier.price / 100; // Convert cents to dollars

          // Apply same discount to alternative price
          if (appliedPromo) {
            if (appliedPromo.type === "percentage") {
              alternativePrice =
                alternativePrice * (1 - appliedPromo.discount / 100);
            } else if (appliedPromo.type === "fixed_amount") {
              alternativePrice = Math.max(
                0,
                alternativePrice - appliedPromo.discount / 100
              );
            }
          }

          // Calculate yearly equivalent of monthly price
          alternativePrice = alternativePrice * 12;
        } else {
          // Fallback: estimate yearly price as 12x monthly price
          alternativePrice = currentPrice * 12;
        }
      } else {
        // Fallback: estimate yearly price as 12x monthly price
        alternativePrice = currentPrice * 12;
      }
    }

    // Calculate static monthly and yearly prices
    let monthlyPrice = 0;
    let yearlyPrice = 0;

    // Calculate monthly price
    if (isYearly) {
      // If yearly is selected, use the monthly plan details
      if (monthlyPlanDetails) {
        const monthlyTier = monthlyPlanDetails?.tiers.find((tier: any) => {
          if (!tier.ending_unit) {
            return quantity >= tier.starting_unit;
          }
          return quantity >= tier.starting_unit && quantity <= tier.ending_unit;
        });

        if (monthlyTier) {
          monthlyPrice = monthlyTier.price / 100; // Convert cents to dollars
          // Apply discount if promo code is applied
          if (appliedPromo) {
            if (appliedPromo.type === "percentage") {
              monthlyPrice = monthlyPrice * (1 - appliedPromo.discount / 100);
            } else if (appliedPromo.type === "fixed_amount") {
              monthlyPrice = Math.max(
                0,
                monthlyPrice - appliedPromo.discount / 100
              );
            }
          }
        }
      }
    } else {
      // If monthly is selected, use the current plan details
      if (planDetails && planDetails.periodUnit === "month") {
        const monthlyTier = planDetails?.tiers.find((tier: any) => {
          if (!tier.ending_unit) {
            return quantity >= tier.starting_unit;
          }
          return quantity >= tier.starting_unit && quantity <= tier.ending_unit;
        });

        if (monthlyTier) {
          monthlyPrice = monthlyTier.price / 100; // Convert cents to dollars
          // Apply discount if promo code is applied
          if (appliedPromo) {
            if (appliedPromo.type === "percentage") {
              monthlyPrice = monthlyPrice * (1 - appliedPromo.discount / 100);
            } else if (appliedPromo.type === "fixed_amount") {
              monthlyPrice = Math.max(
                0,
                monthlyPrice - appliedPromo.discount / 100
              );
            }
          }
        }
      }
    }

    // Calculate yearly price (monthly equivalent)
    if (isYearly) {
      // If yearly is selected, use the current yearly price
      yearlyPrice = discountedCurrentPrice / 12;
    } else {
      // If monthly is selected, use the actual yearly price from alternative plan details
      if (monthlyPlanDetails && monthlyPlanDetails.periodUnit === "year") {
        // Use the fetched yearly plan details
        const yearlyTier = monthlyPlanDetails?.tiers.find((tier: any) => {
          if (!tier.ending_unit) {
            return quantity >= tier.starting_unit;
          }
          return quantity >= tier.starting_unit && quantity <= tier.ending_unit;
        });

        if (yearlyTier) {
          let yearlyPriceValue = yearlyTier.price / 100; // Convert cents to dollars
          // Apply discount if promo code is applied
          if (appliedPromo) {
            if (appliedPromo.type === "percentage") {
              yearlyPriceValue =
                yearlyPriceValue * (1 - appliedPromo.discount / 100);
            } else if (appliedPromo.type === "fixed_amount") {
              yearlyPriceValue = Math.max(
                0,
                yearlyPriceValue - appliedPromo.discount / 100
              );
            }
          }
          yearlyPrice = yearlyPriceValue / 12;
        } else {
          // Fallback: estimate yearly price as 12x monthly price
          yearlyPrice = discountedCurrentPrice;
        }
      } else {
        // Fallback: estimate yearly price as 12x monthly price
        yearlyPrice = discountedCurrentPrice;
      }
    }

    // Calculate savings
    let savings = 0;
    let savingsPercentage = 0;

    if (isYearly) {
      // If yearly is selected, savings = monthly equivalent - yearly price
      // Use original prices for savings calculation
      const originalAlternativePrice =
        alternativePrice / (appliedPromo ? 1 - appliedPromo.discount / 100 : 1);
      const originalCurrentPrice = currentPrice;
      savings = Math.abs(originalAlternativePrice - originalCurrentPrice);

      // Add promo code discount to savings if applied
      if (appliedPromo) {
        if (appliedPromo.type === "percentage") {
          savings += (originalCurrentPrice * appliedPromo.discount) / 100;
        } else if (appliedPromo.type === "fixed_amount") {
          savings += appliedPromo.discount / 100;
        }
      }

      savingsPercentage =
        originalAlternativePrice > 0
          ? Math.round((savings / originalAlternativePrice) * 100)
          : 0;
    } else {
      // If monthly is selected, savings = (monthly price × 12) - actual yearly price
      // Use original prices for savings calculation
      const originalMonthlyPrice =
        monthlyPrice / (appliedPromo ? 1 - appliedPromo.discount / 100 : 1);
      const yearlyEquivalent = originalMonthlyPrice * 12;
      const actualYearlyPrice = monthlyPlanDetails
        ? (monthlyPlanDetails.tiers.find((tier: any) => {
            if (!tier.ending_unit) {
              return quantity >= tier.starting_unit;
            }
            return (
              quantity >= tier.starting_unit && quantity <= tier.ending_unit
            );
          })?.price || 0) / 100
        : 0;

      savings = Math.abs(yearlyEquivalent - actualYearlyPrice);

      // Add promo code discount to savings if applied
      if (appliedPromo) {
        if (appliedPromo.type === "percentage") {
          savings += (actualYearlyPrice * appliedPromo.discount) / 100;
        } else if (appliedPromo.type === "fixed_amount") {
          savings += appliedPromo.discount / 100;
        }
      }

      savingsPercentage =
        actualYearlyPrice > 0
          ? Math.round((savings / actualYearlyPrice) * 100)
          : 0;
    }

    return {
      currentPrice: `$${Math.round(discountedCurrentPrice)}`,
      originalPrice: `$${Math.round(currentPrice)}`,
      alternativePrice: `$${Math.round(alternativePrice)}`,
      savings: `$${Math.round(savings)}`,
      savingsPercentage,
      isYearlySelected: isYearly,
      currentPeriod: isYearly ? "year" : "month",
      alternativePeriod: isYearly ? "month" : "year",
      currentPriceValue: discountedCurrentPrice || 0,
      originalPriceValue: currentPrice || 0,
      alternativePriceValue: alternativePrice || 0,
      monthlyPrice: `$${Math.round(monthlyPrice)}`,
      yearlyPrice: `$${Math.round(yearlyPrice)}`,
    };
  };

  const handlePhoneChange = (value: string) => {
    // Only update the state, don't do any processing
    setFormData((prev) => ({
      ...prev,
      phoneNumber: value,
    }));
  };

  const validatePassword = (password: string) => {
    const minLength = 8;
    const hasNumber = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    const hasLowercase = /[a-z]/.test(password);
    const hasUppercase = /[A-Z]/.test(password);

    if (password.length < minLength) {
      return "Password must be at least 8 characters long";
    }
    if (!hasNumber) {
      return "Password must contain at least one number";
    }
    if (!hasSpecialChar) {
      return "Password must contain at least one special character";
    }
    if (!hasUppercase) {
      return "Password must contain at least one uppercase letter";
    }
    if (!hasLowercase) {
      return "Password must contain at least one lowercase letter";
    }
    return "";
  };

  const validateForm = () => {
    const errors = {
      firstName: "",
      lastName: "",
      phoneNumber: "",
      email: "",
      password: "",
      subdomain: "",
    };
    let isValid = true;

    // Check required fields with hyperlink injection protection
    if (!formData.firstName || !formData.firstName.trim()) {
      errors.firstName = "First name is required";
      isValid = false;
    } else {
      const firstNameValidation = validateAndSanitizeName(formData.firstName, "First name");
      if (!firstNameValidation.isValid) {
        errors.firstName = firstNameValidation.error || "Invalid first name";
        isValid = false;
      } else if (formData.firstName.trim().length > 50) {
        errors.firstName = "First name must be 50 characters or less";
        isValid = false;
      }
    }

    // Last name validation with hyperlink injection protection
    if (formData.lastName && formData.lastName.trim()) {
      const lastNameValidation = validateAndSanitizeName(formData.lastName, "Last name", false);
      if (!lastNameValidation.isValid) {
        errors.lastName = lastNameValidation.error || "Invalid last name";
        isValid = false;
      } else if (formData.lastName.trim().length > 50) {
        errors.lastName = "Last name must be 50 characters or less";
        isValid = false;
      }
    }

    // Email validation - check if empty first, then validate format
    if (!formData.email || !formData.email.trim()) {
      errors.email = "Work email is required";
      isValid = false;
    } else {
      const emailValidation = validateEmail(formData.email);
      if (!emailValidation.isValid) {
        errors.email = emailValidation.error || "Please enter a valid email";
        isValid = false;
      }
    }

    //Phone number validation
    if (!formData.phoneNumber || !formData.phoneNumber.trim()) {
      errors.phoneNumber = "Phone number is required";
      isValid = false;
    } else if (formData.phoneNumber.trim().length > 20) {
      errors.phoneNumber = "Phone number must be 20 characters or less";
      isValid = false;
    }

    // Password validation
    if (!formData.password.trim()) {
      errors.password = "Password is required";
      setPasswordError("Password is required");
      isValid = false;
    } else {
      const passwordValidationError = validatePassword(formData.password);
      if (passwordValidationError) {
        errors.password = passwordValidationError;
        setPasswordError(passwordValidationError);
        isValid = false;
      } else {
        setPasswordError("");
      }
    }

    setFormErrors(errors);
    return isValid;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    // For subdomain field, convert to lowercase
    const processedValue = name === "subdomain" ? value.toLowerCase() : value;

    // Update the form data with the raw input value
    setFormData((prev) => ({
      ...prev,
      [name]: processedValue,
    }));

    // Validate first name with hyperlink injection protection
    if (name === "firstName") {
      if (!value.trim()) {
        setFormErrors((prev) => ({ ...prev, firstName: "First name is required" }));
      } else {
        const nameValidation = validateAndSanitizeName(value, "First name");
        if (!nameValidation.isValid) {
          setFormErrors((prev) => ({ ...prev, firstName: nameValidation.error ?? "Invalid first name" }));
        } else {
          setFormErrors((prev) => ({ ...prev, firstName: "" }));
        }
      }
    }

    // Validate last name with hyperlink injection protection
    if (name === "lastName") {
      if (value.trim()) {
        const nameValidation = validateAndSanitizeName(value, "Last name", false);
        if (!nameValidation.isValid) {
          setFormErrors((prev) => ({ ...prev, lastName: nameValidation.error ?? "Invalid last name" }));
        } else {
          setFormErrors((prev) => ({ ...prev, lastName: "" }));
        }
      } else {
        setFormErrors((prev) => ({ ...prev, lastName: "" }));
      }
    }

    // Clear error when user starts typing (for other fields)
    if (name !== "firstName" && name !== "lastName") {
      setFormErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }

    if (name === "password") {
      if (!value.trim()) {
        setPasswordError("Password is required");
      } else {
        setPasswordError(validatePassword(value));
      }
    }
  };

  const [verificationDialogOpen, setVerificationDialogOpen] =
    React.useState(false);

  const handleVerificationDialogOpen = () => {
    setVerificationDialogOpen(true);
  };

  const handleVerificationDialogClose = async () => {
    setVerificationDialogOpen(false);
    const leadData = {
      leadId: leadSquaredId,
      fields: [
        { Attribute: "mx_Plan_Name", Value: cleanPlanId },
        { Attribute: "mx_Status", Value: "Pending Verification" },
      ],
    };
    await LeadsquaredService.updateLsField(leadData);
    navigate("/login");
  };

  // Helper function to clear UTM params from localStorage
  const clearUtmParamsFromStorage = () => {
    localStorage.removeItem("utm_source");
    localStorage.removeItem("utm_medium");
    localStorage.removeItem("utm_campaign");
    localStorage.removeItem("utm_content");
    localStorage.removeItem("utm_term");
    localStorage.removeItem("ad_id");
    localStorage.removeItem("fbc_id");
    localStorage.removeItem("h_ad_id");
    localStorage.removeItem("utm_id");
    localStorage.removeItem("gc_id");
    localStorage.removeItem("h_ga_id");
    localStorage.removeItem("h_keyword_id");
    localStorage.removeItem("h_keyword");
    localStorage.removeItem("h_placement");
    localStorage.removeItem("g_special_campaign");
  };

  const handleVerificationDialogProceed = async () => {
    // handleVerificationDialogClose();
    setVerificationDialogOpen(false);
    try {
      await LeadsquaredService.joinActivity({
        id: leadSquaredId,
        activityEvent: 205,
        activityValue: "Email Verified",
      });
      const leadData = {
        leadId: leadSquaredId,
        fields: [
          {
            Attribute: "mx_Plan_Name",
            Value: isTrial ? PLAN_NAMES.FREE_TRIAL : cleanPlanId,
          },
          { Attribute: "mx_Status", Value: "Active" },
        ],
      };
      await LeadsquaredService.updateLsField(leadData);
    } catch (error) {
      console.error("Error updating lead data:", error);
    }

    // Clear UTM parameters from localStorage after successful signup
    clearUtmParamsFromStorage();

    // Pass plan data through URL parameters
    const planData = {
      price: planPrice,
      currency: "USD",
      coupon: appliedPromo?.code || "",
      plan_name: planDetails?.name || cleanPlanId || "Unknown Plan",
      customer_type: "with_cc",
    };

    // Encode plan data as URL parameters
    const planDataParams = new URLSearchParams({
      ...planData,
      price: planData.price.toString(),
    }).toString();

    window.location.href = `/loading-dashboard?ew_email=${formData.email}&planId=${isTrial ? PLAN_NAMES.FREE_TRIAL : planId}&${planDataParams}`;
  };

  const BpIcon = styled("span")(({ theme }) => ({
    borderRadius: 4,
    width: 24,
    height: 24,
    boxShadow:
      "inset 0 0 0 1px rgba(16,22,26,.2), inset 0 -1px 0 rgba(16,22,26,.1)",
    backgroundColor: "#fff",

    "input:hover ~ &": {
      border: "1px solid #666666",
    },
  }));

  const BpCheckedIcon = styled(BpIcon)({
    "&::before": {
      display: "block",
      width: 24,
      height: 24,
      backgroundImage:
        "url(\"data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath" +
        " fillRule='evenodd' clipRule='evenodd' d='M12 5c-.28 0-.53.11-.71.29L7 9.59l-2.29-2.3a1.003 " +
        "1.003 0 00-1.42 1.42l3 3c.18.18.43.29.71.29s.53-.11.71-.29l5-5A1.003 1.003 0 0012 5z' fill='%23fff'/%3E%3C/svg%3E\")",
      content: '""',
    },
    "input ~ &": {
      backgroundColor: "#178EFC",
      border: "none",
    },

    "input:hover ~ &": {
      border: "none",
    },
  });

  function BpCheckbox(props: CheckboxProps) {
    return (
      <Checkbox
        sx={{ "&:hover": { bgcolor: "transparent" } }}
        disableRipple
        color="default"
        checkedIcon={<BpCheckedIcon />}
        icon={<BpIcon />}
        {...props}
      />
    );
  }

  const handleSocialSignIn = (provider: SocialProvider) => {
    try {
      const authUrl = config.sso_auth_url;
      const planIdParam = searchParams.get("planId");

      const utmParams = {
        utm_source: localStorage.getItem("utm_source"),
        utm_medium: localStorage.getItem("utm_medium"),
        utm_campaign: localStorage.getItem("utm_campaign"),
        utm_content: localStorage.getItem("utm_content"),
        utm_term: localStorage.getItem("utm_term"),
        ad_id: localStorage.getItem("ad_id"),
        fbc_id: localStorage.getItem("fbc_id"),
        h_ad_id: localStorage.getItem("h_ad_id"),
        utm_id: localStorage.getItem("utm_id"),
        gc_id: localStorage.getItem("gc_id"),
        h_ga_id: localStorage.getItem("h_ga_id"),
        h_keyword_id: localStorage.getItem("h_keyword_id"),
        h_keyword: localStorage.getItem("h_keyword"),
        h_placement: localStorage.getItem("h_placement"),
        g_special_campaign: localStorage.getItem("g_special_campaign"),
      };

      // Filter out null/undefined/empty values from UTM params
      const cleanUtmParams = Object.entries(utmParams)
        .filter(
          ([_, value]) =>
            value !== null && value !== undefined && value.trim() !== ""
        )
        .reduce((acc, [key, value]) => ({ ...acc, [key]: value?.trim() }), {});

      // Create state parameter with additional metadata
      const stateData = {
        isRegistration: true,
        timestamp: Date.now(),
        flow: "first-flow",
        ...(planIdParam && { planId: planIdParam }), // Only include planId if it exists
        ...(Object.keys(cleanUtmParams).length > 0 && {
          utm_params: cleanUtmParams,
        }), // Only include UTM params if they exist
      };

      const baseParams = {
        client_id: config.sso_client_id,
        response_type: "code",
        scope: "email openid profile phone",
        redirect_uri: `${window.location.origin}/auth/sso/callback`,
        state: JSON.stringify(stateData),
        prompt: "select_account",
      };

      const params = new URLSearchParams({
        ...baseParams,
        identity_provider: provider,
      });

      window.location.href = `${authUrl}?${params.toString()}`;
    } catch (error) {
      console.error("Google Sign In Error:", error);
      toast.error("Failed to initialize Google Sign In");
    }
  };

  const handleGoogleSignIn = () => handleSocialSignIn("Google");
  const handleMicrosoftSignIn = () => handleSocialSignIn("Microsoft");
  const handleLinkedinSignIn = () => handleSocialSignIn("Linkedin");

  const handleGetStarted = async () => {
    try {
      setIsLoading(true);

      // Validation and display errors (from handleNext)
      const isValid = validateForm();
      if (!isValid) {
        setIsLoading(false);
        return;
      }

      // Check if email exists (from handleNext)
      const emailCheckResponse = await AuthService.checkEmailExists(
        formData.email
      );
      if (emailCheckResponse.data?.exists) {
        toast.error(
          "An account with this email already exists. Please use a different email or try logging in."
        );
        setIsLoading(false);
        return;
      }

      // Use firstName and lastName from form
      const firstName = (formData.firstName || "").trim();
      const lastName = (formData.lastName || "").trim();

      // Generate subdomain from first name (use form firstName if available, otherwise fallback to email for subdomain only)
      const baseSubdomain = (firstName || generateFirstName(formData.email))
        .toLowerCase()
        .trim()
        .replace(/\s+/g, "");
      const subdomainResponse = await AuthService.generateSubdomain(
        baseSubdomain,
        Applications.EASYWEBINAR
      );

      // Store the generated subdomain for use in the form
      setFormData((prev) => ({
        ...prev,
        subdomain: subdomainResponse.subdomain,
      }));

      // Log signup event (from handleNext)
      try {
        const planId = searchParams.get("planId");
        const eventData = {
          step: SIGNUP_STEPS.FORM,
          password: formData.password,
          planId: cleanPlanId || "",
          planUnits: planId ? planId.match(/-(\d+)$/)?.[1] || 10 : 10,
          signupFlow: SIGNUP_FLOWS.PRICING_PAGE,
        };

        await logEvent(formData.email, EVENT_TYPES.SIGNUP, eventData);
      } catch (error: any) {
        console.error("Error logging signup event:", error);
        // Don't block the flow if event logging fails
      }

      // Create lead in LeadSquared (from handleNext)
      try {
        let utm_Data = {
          utm_source: localStorage.getItem("utm_source"),
          utm_medium: localStorage.getItem("utm_medium"),
          utm_campaign: localStorage.getItem("utm_campaign"),
          utm_content: localStorage.getItem("utm_content"),
          utm_term: localStorage.getItem("utm_term"),
          ad_id: localStorage.getItem("ad_id"),
          fbc_id: localStorage.getItem("fbc_id"),
          h_ad_id: localStorage.getItem("h_ad_id"),
          utm_id: localStorage.getItem("utm_id"),
          gc_id: localStorage.getItem("gc_id"),
          h_ga_id: localStorage.getItem("h_ga_id"),
          h_keyword_id: localStorage.getItem("h_keyword_id"),
          h_keyword: localStorage.getItem("h_keyword"),
          h_placement: localStorage.getItem("h_placement"),
          g_special_campaign: localStorage.getItem("g_special_campaign"),
        };

        // Security: Sanitize input data before sending to LeadSquared
        const sanitizedFirstNameForLS = sanitizeTextInput(firstName);
        const sanitizedLastNameForLS = sanitizeTextInput(lastName);
        
        // Create form data with firstName, lastName, and phone from form
        const formDataWithDetails = {
          firstName: sanitizedFirstNameForLS, // Sanitized first name to prevent hyperlink injection
          lastName: sanitizedLastNameForLS, // Sanitized last name to prevent hyperlink injection
          email: formData.email.trim().toLowerCase(), // Normalize email
          password: formData.password,
          phone: sanitizeTextInput(formData.phoneNumber.trim()), // Sanitize phone
          timezone: getTimezoneString(),
          subdomain: subdomainResponse.subdomain,
        };

        const LsId = await LeadsquaredService.createtLead(
          formDataWithDetails,
          "",
          utm_Data
        );
        await LeadsquaredService.joinActivity({
          id: LsId,
          activityEvent: 201,
          activityValue: "Signup Attempt - pricing",
        });
        const leadData = {
          leadId: LsId,
          fields: [
            { Attribute: "mx_Source_lp", Value: searchParams.get("Source_IP") },
          ],
        };
        await LeadsquaredService.updateLsField(leadData);
        setLeadSquaredId(LsId);
      } catch (error: any) {
        console.error("Error creating lead:", error);
      }

      // Add domain validation here
      if (!subdomainResponse.subdomain.trim()) {
        setFormErrors((prev) => ({
          ...prev,
          subdomain: "Domain is required",
        }));
        setDomainSuggestions([]);
        setIsLoading(false);
        return;
      } else if (!/^[a-zA-Z0-9-]+$/.test(subdomainResponse.subdomain)) {
        setFormErrors((prev) => ({
          ...prev,
          subdomain: "Domain can only contain letters, numbers, and hyphens",
        }));
        setDomainSuggestions([]);
        setIsLoading(false);
        return;
      }

      // Check domain availability first
      const domainResponse = await AuthService.checkSubDomainAvailability(
        subdomainResponse.subdomain,
        Applications.EASYWEBINAR
      );

      if (!domainResponse.available) {
        setFormErrors((prev) => ({
          ...prev,
          subdomain: "This domain is already taken. Please try another one.",
        }));
        // Set domain suggestions if they exist
        if (
          domainResponse.suggestions &&
          domainResponse.suggestions.length > 0
        ) {
          setDomainSuggestions(domainResponse.suggestions);
        } else {
          setDomainSuggestions([]);
        }
        setIsLoading(false);
        return;
      }

      // Clear suggestions if domain is available
      setDomainSuggestions([]);

      let cardTokenResult = null;
      const planId = searchParams.get("planId");

      // Only process card if there's a plan and it's not a free trial
      if (planId && !isTrial) {
        // Check if card has any validation errors
        if (cardError) {
          toast.error("Please fix card validation errors before proceeding");
          setIsLoading(false);
          return;
        }

        const result = await cardRef.current.tokenize();

        if (result.error) {
          setCardError(result.error.message || "Card tokenization failed");
          toast.error("Please check your card details.");
          setIsLoading(false);
          return;
        }

        cardTokenResult = result.token;
        setCardToken(cardTokenResult);
        setCardError(null);

        if (!cardTokenResult) {
          setCardError("Please enter valid card details");
          toast.error("Please enter valid card details");
          setIsLoading(false);
          return;
        }
      }

      // Get UTM parameters directly from URL
      const urlParams = new URLSearchParams(window.location.search);

      const utmParams = {
        utm_source: urlParams.get("utm_source"),
        utm_medium: urlParams.get("utm_medium"),
        utm_campaign: urlParams.get("utm_campaign"),
        utm_content: urlParams.get("utm_content"),
        utm_term: urlParams.get("utm_term"),
        ad_id: urlParams.get("ad_id"),
        fbc_id: urlParams.get("fbc_id"),
        h_ad_id: urlParams.get("h_ad_id"),
        utm_id: urlParams.get("utm_id"),
        gc_id: urlParams.get("gc_id"),
        h_ga_id: urlParams.get("h_ga_id"),
        h_keyword_id: urlParams.get("h_keyword_id"),
        h_keyword: urlParams.get("h_keyword"),
        h_placement: urlParams.get("h_placement"),
        g_special_campaign: urlParams.get("g_special_campaign"),
      };

      // Remove null/undefined values from the UTM params object
      const cleanUtmParams = Object.entries(utmParams)
        .filter(([_, value]) => value !== null && value !== undefined)
        .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

      // Check if buyNow parameter is present and true
      const buyNow = urlParams.get("BuyNow") == "true";

      // Security: Sanitize input data before sending to API
      // Security: Sanitize names before sending to prevent hyperlink injection
      const sanitizedFirstName = sanitizeTextInput(formData.firstName.trim());
      const sanitizedLastName = sanitizeTextInput(formData.lastName?.trim() || "");

      const signupData = {
        ...formData,
        firstName: sanitizedFirstName, // Sanitized first name to prevent hyperlink injection
        lastName: sanitizedLastName, // Sanitized last name to prevent hyperlink injection
        email: formData.email.trim().toLowerCase(), // Normalize email
        subdomain: subdomainResponse.subdomain,
        phoneNumber: sanitizeTextInput(formData.phoneNumber.trim()), // Sanitize phone
        timezone: getTimezoneString(),
        ...(planId &&
          !isTrial && {
            planId: planDetails.id,
            planUnits: parseInt(planId?.match(/-(\d+)$/)?.[1] || "1") || 1,
            cardToken: cardTokenResult,
            couponId: appliedPromo?.code,
          }),
        ...(isTrial && {
          planId: planDetails.id,
        }),
        // Set trialEnd to 0 if buyNow is true
        ...(buyNow && { trialEnd: 0 }),
        // Include UTM parameters if they exist
        ...(Object.keys(cleanUtmParams).length > 0 && {
          utm_params: cleanUtmParams,
        }),
      };

      const response = await AuthService.signup(signupData);
      setVerificationData({
        companyId: response.data.companyId || "",
        companyName: response.data.companyName || "",
        infLeadSquaredId: response.data.inf_lead_squared_id || "",
      });
      handleVerificationDialogOpen();
      // Clear UTM params on successful signup
      clearUtmParamsFromStorage();
      toast.success("Signup successful! Please verify your account.");
      try {
        await LeadsquaredService.joinActivity({
          id: leadSquaredId,
          activityEvent: 205,
          activityValue: "Waiting To Confirm Email Validation",
        });
        if (planDetails.id === "free-trial-plan") {
          await LeadsquaredService.joinActivity({
            id: leadSquaredId,
            activityEvent: 205,
            activityValue: "Free Plan",
          });
        } else {
          await LeadsquaredService.joinActivity({
            id: leadSquaredId,
            activityEvent: 205,
            activityValue: isTrial ? PLAN_NAMES.FREE_TRIAL : planDetails.id,
          });
          await LeadsquaredService.joinActivity({
            id: leadSquaredId,
            activityEvent: 205,
            activityValue: "Currently In Trial",
          });
        }
      } catch (error: any) {
        console.error("Error joining activities:", error);
      }
    } catch (error: any) {
      console.error("Signup failed:", error);
      setDomainSuggestions([]);
      // Clear UTM params on signup failure too
      clearUtmParamsFromStorage();
      if (error.name === "INVALID_CARD") {
        setCardError("Please enter valid card details");
        toast.error("Invalid card details. Please try again.");
      } else {
        toast.error(
          error.message || "An error occurred during signup. Please try again."
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const validateCoupon = async () => {
    if (!promoCode.trim()) {
      setPromoError("Please enter a promo code");
      toast.error("Please enter a promo code");
      return;
    }

    try {
      const planId = searchParams.get("planId");
      const response = await chargebeeService.validateCoupon(
        promoCode,
        planId ? planId.replace(/-\d+$/, "") : undefined
      );

      if (response.data.valid) {
        const { coupon } = response.data;
        setAppliedPromo({
          code: coupon.id,
          discount: coupon.discountPercentage || coupon.discountAmount,
          type: coupon.discountType,
        });
        setPromoError("");
        setShowPromoField(false);
        toast.success("Promo code applied successfully!");
      } else {
        setPromoError(response.data.error || "Invalid promo code");
        setAppliedPromo(null);
        toast.error(response.data.error || "Invalid promo code");
      }
    } catch (error: any) {
      console.error("Error validating coupon:", error);
      setPromoError("Failed to validate promo code");
      setAppliedPromo(null);
      toast.error("Failed to validate promo code. Please try again.");
    }
  };

  // Inside the Signup component, add a state variable for planFeatures
  const [planFeatures, setPlanFeatures] = useState<{
    planType: string;
    currency: string;
    duration: string;
    attendees: number;
    liveAttendees?: number;
    automatedAttendees?: number;
    price: number | string;
    webinars: number | string;
    storage: string;
  } | null>(null);

  // Add new state variables to store the extracted values
  const [planType, setPlanType] = useState<string>("");
  const [leads, setLeads] = useState<number>(0);
  const [liveAttendees, setLiveAttendees] = useState<number | undefined>(
    undefined
  );
  const [automatedAttendees, setAutomatedAttendees] = useState<
    number | undefined
  >(undefined);
  const [planPrice, setPlanPrice] = useState<number | string>("");
  const [planWebinars, setPlanWebinars] = useState<number | string>("");
  const [planStorage, setPlanStorage] = useState<string>("");

  function captureUTMParams() {
    const urlParams = new URLSearchParams(window.location.search);

    const utmSource = urlParams.get("utm_source");
    const utmMedium = urlParams.get("utm_medium");
    const utmCampaign = urlParams.get("utm_campaign");
    const utmContent = urlParams.get("utm_content");
    const utmTerm = urlParams.get("utm_term");
    const email = urlParams.get("email");
    const adId = urlParams.get("ad_id");
    const fbcId = urlParams.get("fbc_id");
    const hAdId = urlParams.get("h_ad_id");
    const utmId = urlParams.get("utm_id");
    const gcId = urlParams.get("gc_id");
    const hGaId = urlParams.get("h_ga_id");
    const hKeywordId = urlParams.get("h_keyword_id");
    const hKeyword = urlParams.get("h_keyword");
    const hPlacement = urlParams.get("h_placement");
    const gSpecialCampaign = urlParams.get("g_special_campaign");

    if (utmSource) localStorage.setItem("utm_source", utmSource);
    if (utmMedium) localStorage.setItem("utm_medium", utmMedium);
    if (utmCampaign) localStorage.setItem("utm_campaign", utmCampaign);
    if (utmContent) localStorage.setItem("utm_content", utmContent);
    if (utmTerm) localStorage.setItem("utm_term", utmTerm);
    if (adId) localStorage.setItem("ad_id", adId);
    if (fbcId) localStorage.setItem("fbc_id", fbcId);
    if (hAdId) localStorage.setItem("h_ad_id", hAdId);
    if (utmId) localStorage.setItem("utm_id", utmId);
    if (gcId) localStorage.setItem("gc_id", gcId);
    if (hGaId) localStorage.setItem("h_ga_id", hGaId);
    if (hKeywordId) localStorage.setItem("h_keyword_id", hKeywordId);
    if (hKeyword) localStorage.setItem("h_keyword", hKeyword);
    if (hPlacement) localStorage.setItem("h_placement", hPlacement);
    if (gSpecialCampaign) localStorage.setItem("g_special_campaign", gSpecialCampaign);
    // Set email from URL parameter if present
    if (email) {
      setFormData((prev) => ({
        ...prev,
        email: email,
      }));
    }
  }

  // Add useEffect for capturing UTM parameters
  useEffect(() => {
    captureUTMParams();
  }, []);

  /**
   * Auto-registration logic when profileId and email are present in URL parameters
   */
  useEffect(() => {
    const handleAutoRegistration = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const profileId = urlParams.get("profileId");
      const email = urlParams.get("email");

      if (profileId && email && !isLoadingPlan && planDetails) {
        try {
          setIsAutoRegistering(true);

          // Get static profile ID
          const staticProfileId = getStaticProfileId();

          // Check if the profileId matches our static ID
          if (profileId === staticProfileId) {
            // Call getLatestSignupEvent API
            const response =
              await EventsLoggerService.getLatestSignupEvent(email);

            if (response.data?.event?.event_data?.password) {
              const password = response.data.event.event_data.password;
              const planId = response.data.event.event_data.planId;
              const planUnits = 10; //response.data.event.event_data.planUnits;

              // Auto-fill the form
              setFormData((prev) => ({
                ...prev,
                email: email,
                password: password,
              }));

              // Generate firstName from email
              const firstName = generateFirstName(email);

              // Proceed with automatic registration using parsed planId and units
              await performAutoRegistration(
                email,
                password,
                firstName,
                planId,
                planUnits
              );
            } else {
              console.error("Password not found in event data");
              toast.error("Unable to retrieve registration data");
            }
          } else {
            console.log(
              "Profile ID does not match, proceeding with normal flow"
            );
            console.log("Expected:", staticProfileId, "Received:", profileId);
          }
        } catch (error: any) {
          console.error("Error during auto-registration:", error);
          toast.error(
            "Auto-registration failed. Please proceed with manual signup."
          );
        } finally {
          setIsAutoRegistering(false);
        }
      }
    };

    handleAutoRegistration();
  }, [isLoadingPlan, planDetails]);

  // Function to get static profile ID
  const getStaticProfileId = () => {
    const STATIC_PROFILE_ID = "550e8400-e29b-41d4-a716-446655440000";
    return STATIC_PROFILE_ID;
  };

  // Function to perform automatic registration
  const performAutoRegistration = async (
    email: string,
    password: string,
    firstName: string,
    planId?: string,
    units?: number
  ) => {
    try {
      setIsLoading(true);

      // Generate subdomain
      const baseSubdomain = firstName.toLowerCase().trim().replace(/\s+/g, "");
      const subdomainResponse = await AuthService.generateSubdomain(
        baseSubdomain,
        Applications.EASYWEBINAR
      );

      // Create lead in LeadSquared
      const utm_Data = {
        utm_source: localStorage.getItem("utm_source"),
        utm_medium: localStorage.getItem("utm_medium"),
        utm_campaign: localStorage.getItem("utm_campaign"),
        utm_content: localStorage.getItem("utm_content"),
        utm_term: localStorage.getItem("utm_term"),
        ad_id: localStorage.getItem("ad_id"),
        fbc_id: localStorage.getItem("fbc_id"),
        h_ad_id: localStorage.getItem("h_ad_id"),
        utm_id: localStorage.getItem("utm_id"),
        gc_id: localStorage.getItem("gc_id"),
        h_ga_id: localStorage.getItem("h_ga_id"),
        h_keyword_id: localStorage.getItem("h_keyword_id"),
        h_keyword: localStorage.getItem("h_keyword"),
        h_placement: localStorage.getItem("h_placement"),
        g_special_campaign: localStorage.getItem("g_special_campaign"),
      };

      // Security: Sanitize input data before sending to API
      const sanitizedFirstName = sanitizeTextInput(firstName.trim());
      
      const formDataWithDetails = {
        firstName: sanitizedFirstName, // Sanitized first name to prevent hyperlink injection
        lastName: "",
        email: email.trim().toLowerCase(), // Normalize email
        password: password,
        timezone: getTimezoneString(),
        subdomain: subdomainResponse.subdomain,
      };

      try {
        const LsId = await LeadsquaredService.createtLead(
          formDataWithDetails,
          "",
          utm_Data
        );
        LeadsquaredService.joinActivity({
          id: LsId,
          activityEvent: 201,
          activityValue: "Auto Signup Attempt",
        });
        const leadData = {
          leadId: LsId,
          fields: [
            { Attribute: "mx_Source_lp", Value: searchParams.get("Source_IP") },
          ],
        };
        await LeadsquaredService.updateLsField(leadData);
        setLeadSquaredId(LsId);
      } catch (error: any) {
        console.error("Error creating lead:", error);
        return;
      }

      // Get UTM parameters from URL
      const urlParams = new URLSearchParams(window.location.search);
      const utmParams = {
        utm_source: urlParams.get("utm_source"),
        utm_medium: urlParams.get("utm_medium"),
        utm_campaign: urlParams.get("utm_campaign"),
        utm_content: urlParams.get("utm_content"),
        utm_term: urlParams.get("utm_term"),
        ad_id: urlParams.get("ad_id"),
        fbc_id: urlParams.get("fbc_id"),
        h_ad_id: urlParams.get("h_ad_id"),
        utm_id: urlParams.get("utm_id"),
        gc_id: urlParams.get("gc_id"),
        h_ga_id: urlParams.get("h_ga_id"),
        h_keyword_id: urlParams.get("h_keyword_id"),
        h_keyword: urlParams.get("h_keyword"),
        h_placement: urlParams.get("h_placement"),
        g_special_campaign: urlParams.get("g_special_campaign"),
      };

      const cleanUtmParams = Object.entries(utmParams)
        .filter(([_, value]) => value !== null && value !== undefined)
        .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

      // Prepare signup data
      const signupData = {
        ...formDataWithDetails,
        phoneNumber: "",
        ...(planId &&
          !isTrial && {
            planId: planId || cleanPlanId, // Use planId from event data or URL
            planUnits: units || 10, // Use parsed units from event data
            couponId: appliedPromo?.code,
          }),
        ...(isTrial && {
          planId: planId || cleanPlanId, // Use planId from event data if available
        }),
        ...(Object.keys(cleanUtmParams).length > 0 && {
          utm_params: cleanUtmParams,
        }),
      };

      // Perform signup
      const response = await AuthService.signup(signupData);

      setVerificationData({
        companyId: response.data.companyId || "",
        companyName: response.data.companyName || "",
        infLeadSquaredId: response.data.inf_lead_squared_id || "",
      });

      // Clear UTM params on successful signup
      clearUtmParamsFromStorage();

      // Join activities
      try {
        await LeadsquaredService.joinActivity({
          id: leadSquaredId,
          activityEvent: 205,
          activityValue: "Waiting To Confirm Email Validation",
        });
        if (planDetails?.id === "free-trial-plan") {
          await LeadsquaredService.joinActivity({
            id: leadSquaredId,
            activityEvent: 205,
            activityValue: "Free Plan",
          });
        } else {
          await LeadsquaredService.joinActivity({
            id: leadSquaredId,
            activityEvent: 205,
            activityValue: isTrial ? PLAN_NAMES.FREE_TRIAL : planDetails?.id,
          });
          await LeadsquaredService.joinActivity({
            id: leadSquaredId,
            activityEvent: 205,
            activityValue: "Currently In Trial",
          });
        }
      } catch (error: any) {
        console.error("Error joining activities:", error);
      }

      logEvent(email, EVENT_TYPES.SIGNUP, {
        step: SIGNUP_STEPS.VERIFICATION,
        planId: planId,
        planUnits: units,
        autoRegistration: true,
      });

      // Open verification dialog
      handleVerificationDialogOpen();
      toast.success(
        "Auto-registration successful! Please verify your account."
      );
    } catch (error: any) {
      console.error("Auto-registration failed:", error);
      toast.error(
        error.message || "Auto-registration failed. Please try manual signup."
      );
    } finally {
      setIsLoading(false);
    }
  };
  // Check for BuyNow parameter on page load
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const buyNow = urlParams.get("BuyNow") === "true";
    setNoTrial(buyNow);
  }, []);

  return (
    <>
      {/* Auto-registration loading overlay */}
      {isAutoRegistering && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
            color: "white",
          }}
        >
          <CircularProgress size={60} sx={{ color: "white", mb: 2 }} />
          <Typography variant="h6" sx={{ color: "white", mb: 1 }}>
            Setting up your account...
          </Typography>
          <Typography variant="body2" sx={{ color: "white", opacity: 0.8 }}>
            Please wait while we complete your registration
          </Typography>
        </Box>
      )}

      <Box
        sx={{
          minHeight: "100vh",
          width: "100vw",
          background: `
                linear-gradient(180deg, #010007 26.92%, #AC8DFF 100%),
                linear-gradient(180deg, #FFFFEF 0%, #FFF 100%)
            `,
          color: "#1f2937",
          fontFamily: "Inter",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: { xs: "16px 24px", sm: "24px 32px" },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <img
              src={LogoDarkMode}
              alt="logo"
              style={{ height: 24, cursor: "pointer", maxWidth: "100%" }}
              onClick={() => window.open("https://easywebinar.com/", "_blank")}
            />
          </Box>
          <Button
            variant="contained"
            endIcon={<ArrowForward />}
            size="small"
            onClick={() => navigate("/login")}
            sx={{
              bgcolor: "#6066E9",
              color: "#fff",
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 600,
              "&:hover": { bgcolor: "#4B4EB5" },
            }}
          >
            Login
          </Button>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: {
              xs: "calc(100vh - 120px)",
              sm: "calc(100vh - 100px)",
              md: "calc(100vh - 80px)",
            },
            p: { xs: 1, sm: 2 },
            pt: { xs: 1, sm: 2, md: 3 },
          }}
        >
          {/* Main container with border - responsive sizing */}
          <Box
            sx={{
              width: { xs: "100%", sm: "90%", md: "1200px", lg: "1400px" },
              maxWidth: { xs: "600px", md: "none" },
              height: { xs: "auto" },
              border: "1.5px solid #e5e7eb",
              borderRadius: "18px",
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              bgcolor: "#ffffff",
              boxShadow: "0 4px 32px 0 rgba(0,0,0,0.1)",
              overflow: "hidden",
            }}
          >
            {/* Left side - Form Section */}
            <Box
              sx={{
                width: { xs: "100%", md: "50%" },
                bgcolor: "#ffffff",
                p: { xs: 3, sm: 4, md: 6 },
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                borderRadius: { xs: "18px", md: "0 18px 18px 0" },
                minHeight: { xs: "500px", md: "auto" },
              }}
            >
              <Box sx={{ textAlign: "center", mb: 2 }}>
                <Typography
                  color="#333333"
                  fontWeight="700"
                  fontSize={{
                    xs: "min(calc(20px + 8 * (100vw - 400px) / (1920 - 400)), 28px)",
                    lg: "28px",
                  }}
                  mb="8px"
                  textAlign="center"
                  lineHeight="1.2"
                >
                  {searchParams.get("flow") === "homepage"
                    ? "Free trial with 200 Live + 1000 Automated Attendees."
                    : "Start Your Free Trial"}
                </Typography>
                <Typography
                  color="#71757D"
                  fontSize={{
                    xs: "min(calc(14px + 2 * (100vw - 400px) / (1920 - 400)), 16px)",
                    lg: "16px",
                  }}
                  fontWeight="600"
                  textAlign="center"
                >
                  After 7-Day Free Trial:{" "}
                  <Box
                    component="span"
                    sx={{ color: "#4e61f6", fontWeight: 700 }}
                  >
                    {" "}
                    {isLoadingPlan ? (
                      <Skeleton
                        variant="text"
                        width={60}
                        height={20}
                        sx={{ display: "inline-block" }}
                      />
                    ) : (
                      <>
                        {isYearly
                          ? (
                            <Box
                              component="span"
                              sx={{ 
                                textDecoration: "line-through",
                                opacity: 0.7,
                                marginRight: "4px"
                              }}
                            >
                              {calculateDynamicPricing().monthlyPrice}
                            </Box>
                          )
                          : ""} 
                        {isYearly
                          ? calculateDynamicPricing().yearlyPrice
                          : calculateDynamicPricing().monthlyPrice}
                        /month
                      </>
                    )}
                  </Box>
                  {isYearly ? " (Billed yearly)" : ""}. Cancel anytime.
                </Typography>
              </Box>

              <Box sx={{ width: "100%" }} className="umbrella-signIn-tabs">
                <CustomTabPanel value={0} index={0}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      mb: 4,
                      mt: { xs: 1, sm: 0 },
                    }}
                  >
                    {/* Billing Period Toggle Switch */}
                    <Box
                        sx={{
                          position: "relative",
                          width: { xs: "280px", sm: "320px", md: "360px" },
                          height: { xs: "50px", sm: "58px", md: "65px" },
                          bgcolor: "#F5F5F5",
                          borderRadius: "20px",
                          cursor: "pointer",
                          overflow: "visible",
                        }}
                        onClick={() => handleBillingPeriodChange(!isYearly)}
                      >
                      {/* Sliding Thumb */}
                      <Box
                          sx={{
                            position: "absolute",
                            top: "3px",
                            left: isYearly ? "3px" : "calc(50% + 1.5px)",
                            width: "calc(50% - 3px)",
                            height: "calc(100% - 6px)",
                            bgcolor: "#000000",
                            borderRadius: "18px",
                            transition: "left 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                            zIndex: 1,
                            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                          }}
                        />

                      {/* Yearly Option */}
                      <Box
                          sx={{
                            position: "absolute",
                            top: "0",
                            left: "0",
                            width: "50%",
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            zIndex: 2,
                          }}
                        >
                        <Typography
                            sx={{
                              color: isYearly ? "#ffffff" : "#1F2937",
                              fontSize: { xs: "16px", sm: "18px", md: "20px" },
                              fontWeight: 600,
                              textAlign: "center",
                              zIndex: 3,
                            }}
                          >
                            Yearly
                          </Typography>

                        {/* Save Badge - Only show when yearly is selected */}

                        {isLoadingPlan ? (
                          <Skeleton
                            variant="rectangular"
                            width={100}
                            height={20}
                            sx={{
                              position: "absolute",
                              bottom: "-14px",
                              borderRadius: "12px",
                              zIndex: 4,
                            }}
                          />
                        ) : (
                          <Box
                              sx={{
                                position: "absolute",
                                bottom: "-14px",
                                bgcolor: "#49ba78",
                                color: "#FFFFFF",
                                fontSize: "11px",
                                fontWeight: 500,
                                px: "8px",
                                py: "4px",
                                borderRadius: "12px",
                                whiteSpace: "nowrap",
                                zIndex: 4,
                              }}
                            >
                            {isYearly
                              ? `Saving ${calculateDynamicPricing().savings } + Free Setup`
                              : `Save ${calculateDynamicPricing().savings} + Free Setup`}
                          </Box>
                        )}
                      </Box>

                      {/* Monthly Option */}
                      <Box
                          sx={{
                            position: "absolute",
                            top: "0",
                            right: "0",
                            width: "50%",
                            height: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            zIndex: 2,
                          }}
                        >
                        <Typography
                            sx={{
                              color: isYearly ? "#1F2937" : "#ffffff",
                              fontSize: { xs: "16px", sm: "18px", md: "20px" },
                              fontWeight: 600,
                              textAlign: "center",
                              zIndex: 3,
                            }}
                          >
                            Monthly
                          </Typography>
                      </Box>
                    </Box>
                  </Box>

                  {/* Promotional Box */}
                  <Box
                    sx={{
                      backgroundColor: "#E6F2FF",
                      borderRadius: "8px",
                      borderLeft: "4px solid #4A90E2",
                      p: 2,
                      mb: 3,
                      position: "relative",
                    }}
                  >
                    <Typography
                      sx={{
                        color: "#4A90E2",
                        fontSize: {
                          xs: "min(calc(13px + 1 * (100vw - 400px) / (1920 - 400)), 15px)",
                          lg: "15px",
                        },
                        lineHeight: 1.6,
                      }}
                    >
                      Exclusive to the Yearly Plan: Enjoy 2 months free, a complimentary webinar funnel setup, and premium add-ons like EasyCRM, Paid Webinars, and the AI Webinar Builder.
                    </Typography>
                  </Box>

                  {!isTrial ? (
                    <>
                      <Grid
                        container
                        rowSpacing={{ xs: 3, sm: 3, md: 3 }}
                        columnSpacing={{ xs: 3, sm: 2, md: 3 }}
                        mb={{ xs: "24px", sm: "18px" }}
                      >
                        {/* Commented out firstName field - will be generated from email */}
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <TextField
                            variant="outlined"
                            label="First name"
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleInputChange}
                            error={!!formErrors.firstName}
                            helperText={formErrors.firstName}
                            required
                            inputProps={{ maxLength: 50 }}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                borderRadius: "3px",
                                borderColor: "#666666",
                              },

                              " .MuiInputLabel-root.Mui-focused": {
                                color: "inherit",
                              },

                              "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                                {
                                  borderColor: "#333333",
                                  border: "1px solid",
                                },

                              "& .MuiOutlinedInput-input": {
                                padding: "14px 16px",
                                background: "#ffffff",
                                borderRadius: "8px",
                              },
                              "&.MuiInputBase-input::placeholder": {
                                color: "#333333",
                                fontSize: "16px",
                                opacity: "0.8",
                              },
                            }}
                            fullWidth
                          />
                        </Grid>

                        {/* Commented out lastName field - will be empty */}
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <TextField
                            variant="outlined"
                            label="Last name"
                            name="lastName"
                            value={formData.lastName}
                            onChange={handleInputChange}
                            error={!!formErrors.lastName}
                            helperText={formErrors.lastName}
                            inputProps={{ maxLength: 50 }}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                borderRadius: "3px",
                                borderColor: "#666666",
                              },

                              " .MuiInputLabel-root.Mui-focused": {
                                color: "inherit",
                              },

                              "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                                {
                                  borderColor: "#333333",
                                  border: "1px solid",
                                },

                              "& .MuiOutlinedInput-input": {
                                padding: "14px 16px",
                                background: "#ffffff",
                                borderRadius: "8px",
                              },
                              "&.MuiInputBase-input::placeholder": {
                                color: "#333333",
                                fontSize: "16px",
                                opacity: "0.8",
                              },
                            }}
                            fullWidth
                          />
                        </Grid>
                      </Grid>

                      <TextField
                        variant="outlined"
                        label="Work email"
                        name="email"
                        placeholder="you@company.com"
                        value={formData.email}
                        onChange={handleInputChange}
                        error={!!formErrors.email}
                        helperText={formErrors.email}
                        disabled={isAutoRegistering}
                        required
                        sx={{
                          mb: "24px",
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "3px",
                            borderColor: "#666666",
                          },

                          " .MuiInputLabel-root.Mui-focused": {
                            color: "inherit",
                          },

                          "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                            {
                              borderColor: "#333333",
                              border: "1px solid",
                            },

                          "& .MuiOutlinedInput-input": {
                            padding: "14px 16px",
                            background: "#ffffff",
                            borderRadius: "8px",
                          },
                          "&.MuiInputBase-input::placeholder": {
                            color: "#333333",
                            fontSize: "16px",
                            opacity: "0.8",
                          },
                        }}
                        fullWidth
                      />

                      <MuiPhone
                        value={formData.phoneNumber}
                        onChange={handlePhoneChange}
                        fullWidth
                        sx={{
                          "& .MuiOutlinedInput-root": {
                          borderRadius: "8px",
                          borderColor: "#C7C7C7",
                          },
                          "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#333333",
                          border: "1px solid",
                          },
                          "& .MuiOutlinedInput-input": {
                          padding: "14px 16px",
                          background: "#ffffff",
                          borderRadius: "8px",
                          },
                          "&.MuiInputBase-input::placeholder": {
                          color: "#333333",
                          fontSize: "16px",
                          opacity: "0.8",
                          },
                        }}
                      />

                      <Box width="100%" mb="8px" mt="24px">
                        <Typography
                          color="#333333"
                          fontSize="14px"
                          fontWeight="500"
                          mb="12px"
                        >
                          Card Details
                        </Typography>
                        <Box
                          width="100%"
                          sx={{
                            position: "relative",
                            "& .CbHosted": {
                              borderRadius: "3px",
                              border: "1px solid rgba(0, 0, 0, 0.23)",
                              padding: "14px 16px",
                              "&:hover, &--focus": {
                                borderColor: "#666666",
                              },
                            },
                          }}
                        >
                          <CardComponent
                            ref={cardRef}
                            currency="USD"
                            showTestCards={true}
                            styles={{
                              border: "1px solid #666666",
                              base: {
                                padding: "14px 16px",
                                border: "1px solid #666666",
                                fontSize: "16px",
                                fontFamily: "Inter, sans-serif",
                                color: "#333333",
                                "::placeholder": {
                                  color: "#667085",
                                },
                              },
                              invalid: {
                                color: "#ff0000",
                              },
                            }}
                            onChange={(status: any) => {
                              console.log("Card status:", status);

                              if (status.error) {
                                setCardError(status.error.message);
                                setIsCardValid(false);
                              } else if (!status.complete) {
                                // Clear error but indicate card is not complete
                                setCardError(null);
                                setIsCardValid(false);
                              } else {
                                // Card is complete and valid
                                setCardError(null);
                                setIsCardValid(true);
                              }
                            }}
                            onReady={() => {
                              console.log("Card component ready");
                              setIsCardReady(true);
                              setIsCardValid(false); // Reset validity when component is ready
                            }}
                            onBlur={() => {
                              console.log("Card component blurred");
                            }}
                            onFocus={() => {
                              console.log("Card component focused");
                            }}
                          />
                        </Box>
                        {cardError && (
                          <Typography color="error" fontSize="12px" mt={1}>
                            {cardError}
                          </Typography>
                        )}
                      </Box>
                      <Box
                        display="flex"
                        justifyContent="end"
                        flexDirection="column"
                      >
                        {!showPromoField ? (
                          <>
                            {appliedPromo ? (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                  padding: "4px 8px",
                                  backgroundColor: "#F0FFF4",
                                  borderRadius: "4px",
                                  width: "fit-content",
                                  marginLeft: "auto",
                                }}
                              >
                                <Typography color="#1D9652" fontSize="14px">
                                  {appliedPromo.code} applied
                                </Typography>
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="16"
                                  height="16"
                                  viewBox="0 0 16 16"
                                  fill="none"
                                  style={{ cursor: "pointer" }}
                                  onClick={() => {
                                    setAppliedPromo(null);
                                    setPromoCode("");
                                  }}
                                >
                                  <path
                                    d="M8 1C4.1 1 1 4.1 1 8C1 11.9 4.1 15 8 15C11.9 15 15 11.9 15 8C15 4.1 11.9 1 8 1ZM10.7 11.5L8 8.8L5.3 11.5L4.5 10.7L7.2 8L4.5 5.3L5.3 4.5L8 7.2L10.7 4.5L11.5 5.3L8.8 8L11.5 10.7L10.7 11.5Z"
                                    fill="#1D9652"
                                  />
                                </svg>
                              </Box>
                            ) : null
                            // <Typography
                            //     color="#2B2B30"
                            //     fontSize="16px"
                            //     display="flex"
                            //     justifyContent="end"
                            //     mb="8px"
                            //     sx={{
                            //         cursor: 'pointer',
                            //         '&:hover': {
                            //             textDecoration: 'underline'
                            //         }
                            //     }}
                            //     onClick={() => setShowPromoField(true)}
                            // >
                            //     Have a promo code?
                            // </Typography>
                            }
                          </>
                        ) : (
                          <TextField
                            variant="outlined"
                            placeholder="Promo Code:"
                            value={promoCode}
                            onChange={(e) => {
                              setPromoCode(e.target.value);
                              setPromoError("");
                            }}
                            error={!!promoError}
                            helperText={promoError}
                            sx={{
                              width: "100%",
                              marginBottom: "25px",
                              "& .MuiOutlinedInput-root": {
                                borderRadius: "3px",
                                borderColor: "#C7C7C7",
                                background: "#ffffff",
                                paddingRight: "0",
                              },
                              "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                                {
                                  borderColor: "#333333",
                                  border: "1px solid",
                                },
                              "& .MuiOutlinedInput-input": {
                                padding: "14px 16px",
                                background: "#ffffff",
                                borderRadius: "8px",
                              },
                              "&.MuiInputBase-input::placeholder": {
                                color: "#333333",
                                fontSize: "16px",
                                opacity: "0.8",
                              },
                            }}
                            slotProps={{
                              input: {
                                endAdornment: (
                                  <InputAdornment
                                    position="end"
                                    sx={{
                                      ".MuiTypography-root": {
                                        color: "#333333",
                                        fontWeight: "500",
                                        fontSize: "16px",
                                      },
                                      paddingRight: "10px",
                                    }}
                                  >
                                    <Box
                                      display="flex"
                                      justifyContent="center"
                                      alignItems="center"
                                    >
                                      <Box
                                        sx={{
                                          background:
                                            theme.palette.primary.main,
                                          borderRadius: "4px",
                                          padding: "4px 10px",
                                          marginRight: "4px",
                                          minWidth: "57px",
                                          fontSize: "14px",
                                          fontWeight: "500",
                                          color: "#fff",
                                          cursor: "pointer",
                                        }}
                                        onClick={validateCoupon}
                                      >
                                        Apply
                                      </Box>
                                      <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="17"
                                        height="16"
                                        viewBox="0 0 17 16"
                                        fill="none"
                                        style={{ cursor: "pointer" }}
                                        onClick={() => {
                                          setShowPromoField(false);
                                          setPromoError("");
                                        }}
                                      >
                                        <path
                                          d="M8.07324 1C4.17324 1 1.07324 4.1 1.07324 8C1.07324 11.9 4.17324 15 8.07324 15C11.9732 15 15.0732 11.9 15.0732 8C15.0732 4.1 11.9732 1 8.07324 1ZM10.7732 11.5L8.07324 8.8L5.37324 11.5L4.57324 10.7L7.27324 8L4.57324 5.3L5.37324 4.5L8.07324 7.2L10.7732 4.5L11.5732 5.3L8.87324 8L11.5732 10.7L10.7732 11.5Z"
                                          fill="#A3A3A3"
                                        />
                                      </svg>
                                    </Box>
                                  </InputAdornment>
                                ),
                              },
                            }}
                          />
                        )}
                      </Box>
                    </>
                  ) : null}
                  <Box
                    sx={{
                      flex: 1,
                      position: "relative",
                      "@media (max-width: 768px)": {
                        width: "100%",
                      },
                    }}
                    display="flex"
                    flexDirection="column"
                    justifyContent="space-between"
                  >
                    <Box>
                      {/* <Box mb="8px">
                        <Typography color='#667085' fontSize='15px' marginBottom="8px">Your Custom URL</Typography>
                        <TextField
                            variant="outlined"
                            placeholder="https://your-brand.easywebinar.live"
                            value={formData.subdomain || ''}
                            onChange={(e) => {
                                setFormData(prev => ({
                                    ...prev,
                                    subdomain: e.target.value.toLowerCase()
                                }));
                                // Clear error when user starts typing
                                setFormErrors(prev => ({
                                    ...prev,
                                    subdomain: ''
                                }));
                            }}
                            error={!!formErrors.subdomain}
                            helperText={formErrors.subdomain}
                            disabled={isAutoRegistering}
                            sx={{
                                width: "100%",
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: '3px',
                                    borderColor: '#C7C7C7',
                                    background: '#ffffff',
                                    paddingRight: '0'
                                },

                                "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                    borderColor: '#333333',
                                    border: '1px solid'
                                },

                                "& .MuiOutlinedInput-input": {
                                    padding: '11px 14px',
                                    background: '#ffffff',
                                    borderRadius: '8px'
                                },
                                "&.MuiInputBase-input::placeholder": {
                                    color: '#333333',
                                    fontSize: '16px',
                                    opacity: '0.8'
                                },
                            }}
                            slotProps={{
                                input: {
                                    endAdornment: (
                                        <InputAdornment
                                            position="end"
                                            sx={{
                                                ".MuiTypography-root": {
                                                    color: "#333333",
                                                    fontWeight: "500",
                                                    fontSize: "16px",
                                                },

                                                paddingRight: "19px",
                                            }}
                                        >
                                            .easywebinar.live
                                        </InputAdornment>
                                    ),
                                },
                            }}
                        />
                    </Box> */}
                    {/* {domainSuggestions.length > 0 && (
                        <Box mt={1} mb={2}>
                            <Typography color="primary" fontSize="12px" mb={1}>
                                Available alternatives:
                            </Typography>
                            <Box display="flex" gap={0.5} flexWrap="wrap">
                                {domainSuggestions.map((suggestion, index) => (
                                    <Button
                                        key={index}
                                        variant="outlined"
                                        size="small"
                                        onClick={() => {
                                            setFormData(prev => ({
                                                ...prev,
                                                subdomain: suggestion
                                            }));
                                            setFormErrors(prev => ({
                                                ...prev,
                                                subdomain: ''
                                            }));
                                            setDomainSuggestions([]);
                                        }}
                                        sx={{
                                            textTransform: 'none',
                                            borderColor: '#D0D5DD',
                                            color: '#344054',
                                            padding: '2px 8px',
                                            minHeight: '24px',
                                            fontSize: '12px',
                                            '&:hover': {
                                                borderColor: '#667085',
                                                backgroundColor: '#F9FAFB'
                                            }
                                        }}
                                    >
                                        {suggestion}
                                    </Button>
                                ))}
                            </Box>
                        </Box>
                    )}
                    {!planId && <Box borderRadius="8px" bgcolor="#EDF6FF" p="24px 12px" color="#5A5475" >
                        <Typography variant="body2" fontSize="min(calc(14px + (16 - 14) * (100vw - 320px) / (1920 - 320)), 16px)">URL Guidelines:</Typography>
                        <Box>
                            <Box component="ul" sx={{ "& li": { listStyle: "disc", fontSize: "min(calc(14px + (16 - 14) * (100vw - 320px) / (1920 - 320)), 16px)" } }} pl="20px" mt="24px" lineHeight="1.5">
                                <Box component="li">
                                    Use lowercase letters, numbers and hyphens only
                                </Box>
                                <Box component="li">
                                    3-30 characters long
                                </Box>
                                <Box component="li">
                                    No spaces or special characters
                                </Box>
                                <Box component="li">
                                    Cannot start or end with a hyphen
                                </Box>
                            </Box>
                        </Box>
                    </Box>} */}
                      <Box
                        display="flex"
                        gap={{ xs: "13px", sm: "119px" }}
                        justifyContent="center"
                        alignItems="center"
                        mt={{ xs: "20px", md: "42px" }}
                      >
                        <Button
                          variant="contained"
                          onClick={handleGetStarted}
                          className="x-large-btn"
                          disabled={
                            isLoading ||
                            isAutoRegistering ||
                            (!isTrial && !isCardValid)
                          }
                          disableElevation
                          fullWidth
                          sx={{
                            textTransform: "capitalize",
                            px: "44px",
                            height: "47px!important",
                            fontSize: "18px",
                            bgcolor: "#0382F7 !important",
                            "&:hover": { bgcolor: "#006dd1 !important" },
                            "&.Mui-disabled": {
                              bgcolor: "#cccccc !important",
                              color: "#666666 !important",
                            },
                          }}
                        >
                          {isLoading || isAutoRegistering ? (
                            <CircularProgress
                              size={24}
                              sx={{ color: "white" }}
                            />
                          ) : !isTrial && !isCardValid ? (
                            "Please complete card details"
                          ) : (
                            "Start 7-day free trial"
                          )}
                        </Button>
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        textAlign: "center",
                        mt: 2,
                        mb: 2,
                      }}
                    >
                      {!noTrial && (
                        <Typography
                          variant="body2"
                          sx={{
                            color: "#374151",
                            fontSize: "14px",
                            fontWeight: 400,
                            lineHeight: 1.5,
                          }}
                        >
                          100% Free Trial • Cancel Anytime • No Hidden Fees
                        </Typography>
                      )}
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#374151",
                          fontSize: "10px",
                          fontWeight: 400,
                          lineHeight: 1.5,
                          mt: 2,
                        }}
                      >
                        By starting your {noTrial ? "plan" : "trial"}, you agree
                        to our{" "}
                        <a
                          href="https://easywebinar.com/terms-conditions/"
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            color: "inherit !important",
                            textDecoration: "underline",
                          }}
                        >
                          Terms of Service
                        </a>{" "}
                        and{" "}
                        <a
                          href="https://easywebinar.com/privacy/"
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            color: "inherit !important",
                            textDecoration: "underline",
                          }}
                        >
                          Privacy Policy
                        </a>
                      </Typography>
                    </Box>
                  </Box>
                </CustomTabPanel>
              </Box>
            </Box>

            {/* Right side - Promotional Section */}
            <Box
              sx={{
                width: "50%",
                bgcolor: "#f8fafc",
                p: { xs: 3, sm: 4, md: 6 },
                display: { xs: "none", md: "flex" },
                flexDirection: "column",
                justifyContent: "center",
                borderRadius: { xs: "18px", md: "18px 0 0 18px" },
                minHeight: { xs: "500px", md: "auto" },
              }}
            >
              <Box sx={{ textAlign: "left" }} margin="auto">
                {/* Benefits Section */}
                <Box sx={{ mb: 8 }}>
                  <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
                    Over 200K webinars hosted, 69M+ registrations, and show-up
                    rates as high as 75%.
                  </Typography>
                  <Box
                    sx={{
                      fontSize: "20px",
                      fontWeight: 500,
                      opacity: 0.7,
                      lineHeight: 1.8,
                    }}
                  >
                    <Typography sx={{ mb: 1 }}>
                      ✓ Monetize any live or evergreen webinar with zero
                      earnings share.
                    </Typography>
                    <Typography sx={{ mb: 1 }}>
                      ✓ The only webinar platform with a built-in sales CRM.
                    </Typography>
                    <Typography>✓ 30-day money-back guarantee.</Typography>
                  </Box>
                </Box>

                {/* Testimonial Card */}
                <Box
                  sx={{
                    background:
                      "linear-gradient(180deg, #060F36 -5.87%, #178EFC 99.93%)",
                    borderRadius: "8px",
                    p: 4,
                    mb: 5,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      gap: 3,
                      alignItems: "center",
                      flexDirection: { xs: "column", sm: "row" },
                    }}
                  >
                    {/* Testimonial Text */}
                    <Box sx={{ flex: 1 }}>
                      {/* 5 Stars */}
                      <Box sx={{ mb: 1.5, height: "13px" }}>
                        <img
                          src="https://easywebinar-site.easywebinar.com/images/stars-sm.png"
                          alt="G2 Badge"
                          style={{ maxWidth: "400px" }}
                        />
                      </Box>

                      <Typography
                        sx={{
                          color: "white",
                          mb: 2,
                          fontSize: "16px",
                          lineHeight: 1.3,
                        }}
                      >
                        "I personally use EasyWebinar and they are the best. I
                        moved from using other platforms and nothing compares to
                        their reliability and customer support!"
                      </Typography>

                      <Box>
                        <Typography
                          sx={{
                            color: "white",
                            fontWeight: 600,
                            fontSize: "18px",
                          }}
                        >
                          Amy Porterfield
                        </Typography>
                        <Typography sx={{ color: "white", fontSize: "16px" }}>
                          Online Marketing Expert
                        </Typography>
                      </Box>
                    </Box>

                    {/* Photo */}
                    <Box
                      sx={{
                        width: "215px",
                        height: "215px",
                        borderRadius: "12px",
                        overflow: "hidden",
                        flexShrink: 0,
                      }}
                    >
                      <img
                        src="https://easywebinar-site.easywebinar.com/images/amy-porterfield-signup.png"
                        alt="G2 Badge"
                        style={{ maxWidth: "400px" }}
                      />
                    </Box>
                  </Box>

                  {/* G2 Badges Section */}
                  <Box
                    sx={{
                      borderTop: "1px solid rgba(255,255,255,0.24)",
                      pt: 3,
                      mt: 5,
                      textAlign: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        color: "white",
                        mb: 2,
                        fontSize: "16px",
                        fontWeight: 500,
                      }}
                    >
                      Recognized by customers across the world as
                      <br />a leader in multiple categories on G2
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1.7,
                        justifyContent: "center",
                        alignItems: "center",
                        flexWrap: "wrap",
                      }}
                    >
                      {/* Badge 1 */}
                      <img
                        src="https://easywebinar-site.easywebinar.com/images/performance-logo.png"
                        alt="G2 Badge"
                        style={{ maxWidth: "400px" }}
                      />
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
      <AccountVerification
        onClose={handleVerificationDialogClose}
        open={verificationDialogOpen}
        onProceed={handleVerificationDialogProceed}
        phoneNumber=""
        email={formData.email}
        password={formData.password}
        companyId={verificationData.companyId}
        companyName={verificationData.companyName}
        infLeadSquaredId={verificationData.infLeadSquaredId}
      />
    </>
  );
};
export default Signup;
